import { useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { LinkLogo } from '@/components/nav/link-logo';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useAppForm } from '@/hooks/use-app-form';

const resetPasswordSchema = z
  .object({
    newPassword: z.string({ message: 'Please enter a new password.' }).min(8, {
      message: 'Password must be at least eight characters.',
    }),
    confirmPassword: z.string({ message: 'Please confirm your password.' }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export const Route = createFileRoute('/reset-password')({
  component: ResetPassword,
  validateSearch: z.object({
    token: z.string().optional(),
    error: z.string().optional(),
  }),
  beforeLoad: async ({ context }) => {
    if (context.isAuthenticated()) {
      throw redirect({ to: '/' });
    }
  },
});

function ResetPassword() {
  const { token, error } = Route.useSearch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const form = useAppForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    } as ResetPasswordFormValues,
    validators: { onSubmit: resetPasswordSchema },
    onSubmit: async ({ value }: { value: ResetPasswordFormValues }) => {
      if (!token) return;
      setIsLoading(true);
      const { error: resetError } = await authClient.resetPassword({
        newPassword: value.newPassword,
        token,
      });
      setIsLoading(false);
      if (resetError) {
        toast.error('Reset failed', {
          description:
            resetError.message || 'Failed to reset password. The link may have expired.',
        });
      } else {
        toast.success('Password reset', {
          description: 'Your password has been reset successfully.',
        });
        navigate({ to: '/sign-in' });
      }
    },
  });

  const hasError = error === 'INVALID_TOKEN';
  const hasToken = Boolean(token);

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-sm">
          <div className="flex flex-col items-center">
            <LinkLogo />
            <h1 className="mt-12 text-3xl font-bold">Reset Your Password</h1>
          </div>

          <div className="mt-12">
            {hasError ? (
              <div className="space-y-4 text-center">
                <Alert
                  title="Invalid or Expired Link"
                  subTitle="This password reset link is invalid or has expired. Please request a new one."
                  variant="error"
                />
                <Link to="/forgot-password">
                  <Button className="w-full" variant="outline">
                    Request New Link
                  </Button>
                </Link>
              </div>
            ) : !hasToken ? (
              <div className="space-y-4 text-center">
                <Alert
                  title="Missing Token"
                  subTitle="No reset token found. Please request a password reset link."
                  variant="error"
                />
                <Link to="/forgot-password">
                  <Button className="w-full" variant="outline">
                    Request Reset Link
                  </Button>
                </Link>
              </div>
            ) : (
              <form
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
              >
                <form.AppForm>
                  <form.AppField name="newPassword">
                    {(field) => (
                      <field.Input
                        label="New Password"
                        className="h-10"
                        type="password"
                        placeholder="Enter a New Password..."
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="confirmPassword">
                    {(field) => (
                      <field.Input
                        label="Confirm Password"
                        className="h-10"
                        type="password"
                        placeholder="Confirm Your New Password..."
                      />
                    )}
                  </form.AppField>
                </form.AppForm>
                <Button
                  className="w-full"
                  size="lg"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground font-medium">
              Remember your password?{' '}
              <Link to="/sign-in">
                <Button className="px-1" variant="link">
                  {' '}
                  Sign In{' '}
                </Button>
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
